import { createAsyncThunk, createSelector, createSlice, isAnyOf } from '@reduxjs/toolkit';
import { restApi } from '@icp/settings';
import { isThisWeek, isThisYear, isToday } from '@icp/utils';
import { createMessage, MessageType } from '../../utils';
import { SESSION_DATE_CATEGORY, VIEW_ENUM } from '../../consts';

const Convertor = {
  Agent: {
    fromDTO: (agent) => {
      const formEntityData = agent;
      if (!formEntityData) {
        return null;
      }
      const { agentName, avatar, description } = formEntityData;
      return {
        id: formEntityData.id,
        name: agentName,
        avatar: avatar?.resources?.[0]?.url,
        description: description?.longText,
        data: formEntityData,
      };
    },
  },
  Session: {
    fromDTO: (session) => {
      const date = session.created_at;
      const dateCategory = (() => {
        if (isToday(date)) return SESSION_DATE_CATEGORY.TODAY;
        if (isThisWeek(date)) return SESSION_DATE_CATEGORY.THIS_WEEK;
        if (isThisYear(date)) return SESSION_DATE_CATEGORY.THIS_YEAR;
        return SESSION_DATE_CATEGORY.OTHER;
      })();

      return {
        id: session.session_id,
        title: session.session_summary,
        date,
        assistantId: session.agent_id,
        dateCategory,
      };
    },
  },
  Message: {
    fromDTO: (message) => {
      return {
        id: message.id,
        assistant_id: message.agent_id,
        created_at: +new Date(message.created_at),
        content: [
          {
            text: message.message,
          },
        ],
        role: message.role,
      };
    },
  },
};

const selectThis = (state) => state[slice.name];
export const selectActiveView = createSelector(selectThis, (state) => state.activeView);
export const selectAgentListLoaded = createSelector(selectThis, (state) => state.agentListLoaded);
export const selectAgentList = createSelector(selectThis, (state) => state.agentList);
export const selectCurrentAgent = createSelector(selectThis, (state) => state.currentAgent);
export const selectSessionListLoaded = createSelector(selectThis, (s) => s.sessionListLoaded);
export const selectSessionList = createSelector(selectThis, (state) => state.sessionList);
export const selectCurrentSession = createSelector(selectThis, (state) => state.currentSession);
export const selectCurrentSessionId = createSelector(selectCurrentSession, (cs) => cs?.id);
export const selectModelListLoaded = createSelector(selectThis, (state) => state.modelListLoaded);
export const selectModelList = createSelector(selectThis, (state) => state.modelList);
export const selectCurrentModel = createSelector(selectThis, (state) => state.currentModel);
export const selectPending = createSelector(selectThis, (state) => state.pending);
export const selectProgressInfo = createSelector(selectThis, (state) => state.progressInfo);
export const selectMessagesLoaded = createSelector(selectThis, (state) => state.messagesLoaded);
export const selectMessages = createSelector(selectThis, (state) => state.messages);
export const selectMessagesCount = createSelector(selectMessages, (messages) => messages.length);
export const selectSkill = createSelector(selectThis, (state) => state.skill);

const LoaderCache = {
  getLoaderOrElse(cacheKey, createLoader) {
    const loader = this[cacheKey];
    if (loader) {
      return loader;
    }
    const loaderPromise = Promise.resolve(createLoader());
    this[cacheKey] = loaderPromise;
    return loaderPromise.catch((error) => {
      this[cacheKey] = null;
      throw error;
    });
  },
  remove(cacheKey) {
    this[cacheKey] = null;
  },
};

export const fetchAgentListOneTime = createAsyncThunk(
  'agent/fetchAgentListOneTime',
  async (payload, { signal, getState, extra }) => {
    const agentListLoaded = selectAgentListLoaded(getState());
    if (agentListLoaded) {
      return selectAgentList(getState());
    }
    const loader = LoaderCache.getLoaderOrElse('agent-list', () =>
      restApi
        .get(extra.apiUrls.getAgentList, {
          signal,
          params: Object.fromEntries(new URLSearchParams(window.location.search).entries()),
        })
        .then((res) => res.agents || []),
    );
    const agentDTOList = await loader;
    return agentDTOList.map(Convertor.Agent.fromDTO).filter(Boolean);
  },
);

export const fetchSessionList = createAsyncThunk(
  'agent/fetchSessionList',
  async (payload, { signal, getState, extra }) => {
    const sessionListLoaded = selectSessionListLoaded(getState());
    if (sessionListLoaded) {
      return selectSessionList(getState());
    }
    const loader = LoaderCache.getLoaderOrElse('session-list', () =>
      restApi
        .get(extra.apiUrls.getThreadList, {
          signal,
          params: Object.fromEntries(new URLSearchParams(window.location.search).entries()),
        })
        .then((res) => res?.data || [])
        .finally(() => LoaderCache.remove('session-list')),
    );
    const sessionList = await loader;
    return sessionList.map(Convertor.Session.fromDTO);
  },
);

export const deleteSession = createAsyncThunk('agent/deleteSession', async (payload) => {
  const session = payload;
  await restApi.post(
    `/flow/api/flow-rest/ceta-agent/delete-session`,
    {},
    {
      params: {
        ...Object.fromEntries(new URLSearchParams(window.location.search).entries()),
        session_id: session.id,
      },
    },
  );
});

export const fetchModelListOneTime = createAsyncThunk(
  'agent/fetchModelListOneTime',
  async (payload, { signal, getState, extra }) => {
    const modelListLoaded = selectModelListLoaded(getState());
    if (modelListLoaded) {
      return selectModelList(getState());
    }
    const loader = LoaderCache.getLoaderOrElse('model-list', () =>
      restApi
        .get(extra.apiUrls.getModelList, {
          signal,
          params: Object.fromEntries(new URLSearchParams(window.location.search).entries()),
        })
        .then((res) => res || []),
    );
    const modelList = await loader;
    return modelList;
  },
);

export const fetchModelListBySession = createAsyncThunk(
  'agent/fetchModelListBySession',
  async (payload, { signal, getState, extra }) => {
    const currentSessionId = selectCurrentSessionId(getState());
    const currentAgent = selectCurrentAgent(getState());
    const loader = restApi
      .get(extra.apiUrls.getModelList, {
        signal,
        params: {
          session_id: currentSessionId,
          agent_id: currentAgent.id,
        },
      })
      .then((res) =>
        (res?.llms || []).map((item) => ({
          ...item,
          model: item.id,
          name: `${item.channel}/${item.llmName}`,
        })),
      );
    const modelList = await loader;
    return modelList;
  },
);

export const fetchMessageList = createAsyncThunk(
  'agent/fetchMessageList',
  async (payload, { dispatch, getState, signal, extra }) => {
    const currentSessionId = selectCurrentSessionId(getState());

    const messageList$ = restApi
      .get(extra.apiUrls.getMessageList, {
        signal,
        params: {
          ...Object.fromEntries(new URLSearchParams(window.location.search).entries()),
          session_id: currentSessionId,
        },
      })
      .then((res) => res.data || [])
      .then((list) => list.map(Convertor.Message.fromDTO));

    const [messageList] = await Promise.all([
      messageList$,
      dispatch(fetchAgentListOneTime()),
      //
    ]);

    const agentList = selectAgentList(getState());

    const messages = messageList.map((msg) => {
      const msgContent = msg.content?.[0]?.text ?? '';
      return msg.role === 'user'
        ? createMessage(MessageType.Prompt, { prompt: msgContent }, true)
        : createMessage(MessageType.Answer, { text: msgContent }, false);
    });

    // 从后往前倒查匹配 assistantId -> agent, 查不到就置空让用户再选一个agent
    let inferredAgent = messageList
      .map((rawMsg) => rawMsg.assistant_id)
      .filter(Boolean)
      .reverse()
      .reduce((found, assistantId) => {
        if (found) return found;
        return agentList.find((agent) => String(agent.id) === String(assistantId));
      }, null);

    // 只有一个agent时，自动选中
    if (agentList.length === 1 && !inferredAgent) {
      inferredAgent = agentList[0];
    }

    if (inferredAgent) {
      await dispatch(chooseAgent({ agent: inferredAgent }));
    } else {
      payload.onAgentMissing?.();
    }

    return { messages, missingAgent: !inferredAgent };
  },
);

export const initActiveView = createAsyncThunk(
  'agent/initActiveView',
  async (payload, { dispatch, getState }) => {
    const { useAgents } = payload;
    if (!useAgents) {
      // 关闭agent功能的直接显示消息列表
      dispatch(setActiveView({ view: VIEW_ENUM.MESSAGE }));
      return;
    }

    await dispatch(fetchSessionList());
    const sessionList = selectSessionList(getState());

    if (sessionList.length === 0) {
      // 没历史会话的，自动新会话
      dispatch(startNewChat());
      dispatch(setActiveView({ view: VIEW_ENUM.AGENT }));
      return;
    }

    // 有历史会话的，也自动新会话
    dispatch(startNewChat());
    dispatch(setActiveView({ view: VIEW_ENUM.AGENT }));
  },
  {
    condition: (payload, { getState }) => {
      const activeView = selectActiveView(getState());
      return activeView == null;
    },
  },
);

export const chooseAgent = (payload) => (dispatch) => {
  const { agent } = payload;
  return dispatch(setCurrentAgent(agent));
};

const slice = createSlice({
  name: 'agent',
  initialState: {
    activeView: null, // keyof VIEW_ENUM

    sessionListLoaded: false,
    sessionList: [],
    currentSession: null,

    agentListLoaded: false,
    agentList: [],
    currentAgent: null,

    modelListLoaded: false,
    modelList: [],
    currentModel: null,

    messagesLoaded: false,
    messages: [],

    // pending单指AI问答请求中
    pending: false,
    // 由type为PROGRESS的SSE消息得来，用于显示当前进度状态信息。在收到下一条其它消息后重置为空。
    progressInfo: null,

    // 'kb' - 知识问答, null - 普通对话
    skill: null,
  },
  reducers: {
    setPending: (state, action) => {
      state.pending = action.payload;
    },
    setProgressInfo: (state, action) => {
      state.progressInfo = action.payload;
    },
    setActiveView: (state, action) => {
      const { view: activeView, skipAutoReload = false } = action.payload;
      state.activeView = activeView;
      if (activeView === VIEW_ENUM.SESSION) {
        state.currentSession = null;
        state.currentAgent = null;

        state.currentModel = null;

        state.pending = false;
        state.messages = [];
        state.messagesLoaded = false;

        if (!skipAutoReload) {
          // 切session view触发重载session list
          state.sessionListLoaded = false;
          state.sessionList = [];
        }
      }
    },
    setCurrentSession: (state, action) => {
      state.currentSession = action.payload;
      state.activeView = VIEW_ENUM.MESSAGE;
    },
    setCurrentModel: (state, action) => {
      state.currentModel = action.payload;
    },
    setCurrentAgent: (state, action) => {
      state.currentAgent = action.payload;
      state.activeView = VIEW_ENUM.MESSAGE;
    },
    setAgentAvatarBlob: (state, action) => {
      [...state.agentList, state.currentAgent]
        .filter(Boolean)
        .filter((agent) => agent.avatar === action.payload.avatar)
        .forEach((agent) => {
          agent.avatarBlob = action.payload.avatarBlob;
        });
    },
    appendMessages: (state, action) => {
      state.messages = [...state.messages, ...action.payload];
    },
    clearMessages: (state) => {
      state.messages = [];
    },
    clearMessagesByType: (state, action) => {
      state.messages = state.messages.filter((msg) => msg.type !== action.payload);
    },
    replaceMessageById: (state, action) => {
      const index = state.messages.findIndex((msg) => msg.id === action.payload.id);
      if (index >= 0) {
        state.messages[index] = action.payload;
      }
    },
    startNewChat: (state) => {
      state.currentSession = null;
      state.currentAgent = null;
      state.currentModel = null;
      state.pending = false;
      state.messages = [];
      state.messagesLoaded = true;
    },
    setMessageFeedback: (state, action) => {
      const { messageId, feedback } = action.payload;
      const index = state.messages.findIndex((msg) => msg.id === messageId);
      if (index >= 0) {
        state.messages[index] = {
          ...state.messages[index],
          content: {
            ...state.messages[index].content,
            feedback,
          },
        };
      }
    },
    setSkillKB: (state, action) => {
      state.skill = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(initActiveView.rejected, (state) => {
        state.activeView = VIEW_ENUM.AGENT;
      })
      .addCase(fetchSessionList.pending, (state) => {
        state.sessionListLoaded = false;
      })
      .addCase(fetchSessionList.fulfilled, (state, action) => {
        state.sessionList = action.payload;
        state.sessionListLoaded = true;
      })
      .addCase(fetchSessionList.rejected, (state) => {
        state.sessionList = [];
        state.sessionListLoaded = true;
      })
      .addCase(deleteSession.fulfilled, (state, action) => {
        const sessionDeleted = action.meta.arg;
        state.sessionList = state.sessionList.filter((session) => session.id !== sessionDeleted.id);
      })
      .addCase(fetchAgentListOneTime.pending, (state) => {
        state.agentListLoaded = false;
      })
      .addCase(fetchAgentListOneTime.fulfilled, (state, action) => {
        state.agentList = action.payload;
        state.agentListLoaded = true;
      })
      .addCase(fetchAgentListOneTime.rejected, () => {
        // do nothing
      })
      .addCase(fetchMessageList.pending, (state) => {
        state.messagesLoaded = false;
      })
      .addCase(fetchMessageList.fulfilled, (state, action) => {
        const { messages, missingAgent } = action.payload;
        state.messagesLoaded = true;
        state.messages = messages;
        if (missingAgent) {
          state.currentAgent = null;
          state.activeView = VIEW_ENUM.AGENT;
        }
      })
      .addCase(fetchMessageList.rejected, (state) => {
        state.messagesLoaded = true;
        state.messages = [];
      })
      .addMatcher(
        isAnyOf(
          fetchModelListOneTime.pending,
          fetchModelListBySession.pending,
          //
        ),
        (state) => {
          state.modelListLoaded = false;
          state.modelList = [];
          state.currentModel = null;
        },
      )
      .addMatcher(
        isAnyOf(
          fetchModelListOneTime.fulfilled,
          fetchModelListBySession.fulfilled,
          //
        ),
        (state, action) => {
          state.modelList = action.payload;
          state.modelListLoaded = true;
        },
      )
      .addMatcher(
        isAnyOf(
          fetchModelListOneTime.rejected,
          fetchModelListBySession.rejected,
          //
        ),
        () => {
          // do nothing
        },
      );
  },
});

export const {
  setPending,
  setProgressInfo,
  setActiveView,
  setCurrentSession,
  setCurrentModel,
  setCurrentAgent,
  setAgentAvatarBlob,
  appendMessages,
  clearMessages,
  clearMessagesByType,
  replaceMessageById,
  startNewChat,
  setMessageFeedback,
  setAgentFunctions,
  setSkillKB,
  //
} = slice.actions;

export default slice;
